import { Receipt } from '../types/order'

/**
 * Shared utility functions for receipt handling
 */
export class ReceiptUtils {
  /**
   * Check if a receipt is a refund receipt
   */
  private static isRefundReceipt(receiptData: Receipt): boolean {
    return receiptData.receipt_id?.startsWith('RFD-') === true
  }

  /**
   * Build refund receipt URL (for view/share/print)
   */
  private static buildRefundReceiptUrl(receiptData: Receipt, includePrint: boolean = false): string {
    if (receiptData.receipt_url) {
      const url = new URL(receiptData.receipt_url, window.location.origin)
      url.pathname = `/receipt/refund/${receiptData.receipt_id}`
      url.search = includePrint ? '?print=1' : ''
      return url.toString()
    } else {
      const printParam = includePrint ? '?print=1' : ''
      return `${window.location.origin}/receipt/refund/${receiptData.receipt_id}${printParam}`
    }
  }
  /**
   * Format price for display
   */
  static formatPrice(amount: number | string): string {
    // If it's already a formatted string, return it as is
    if (typeof amount === 'string') {
      return amount
    }
    // If it's a number, format it
    return `€${amount.toFixed(2)}`
  }

  /**
   * Format date for display
   */
  static formatDate(date: Date | string): string {
    // If it's already a formatted string, return it as is
    if (typeof date === 'string') {
      return date
    }
    // If it's a Date object, format it
    return date.toLocaleString()
  }


  /**
   * Open receipt in print window using the receipt URL
   */
  static printReceipt(receiptData: Receipt): void {
    try {
      let printUrl: string

      if (receiptData.receipt_url) {
        // Normalize to /receipt/{id}?type=CUSTOMER&print=1 as per API spec
        const url = new URL(receiptData.receipt_url, window.location.origin)
        url.pathname = `/receipt/${receiptData.receipt_id}`
        url.searchParams.set('type', 'CUSTOMER')
        url.searchParams.set('print', '1')
        printUrl = url.toString()
      } else {
        // Fallback when receipt_url is missing
        printUrl = `/receipt/${receiptData.receipt_id}?type=CUSTOMER&print=1`
      }

      const printWindow = window.open(printUrl, '_blank')
      if (!printWindow) {
        alert('Failed to open print window. Please check if popups are blocked.')
      }
    } catch (error) {
      alert('Error opening receipt. Please try again.')
    }
  }

  /**
   * Open refund receipt in print window
   * Uses /receipt/refund/{receiptId}?print=1 as per API spec.
   * Assumes receipt_id is the refund ID (RFD-...).
   */
  static printRefundReceipt(receiptData: Receipt): void {
    try {
      const printUrl = this.buildRefundReceiptUrl(receiptData, true)
      const printWindow = window.open(printUrl, '_blank')
      if (!printWindow) {
        alert('Failed to open print window. Please check if popups are blocked.')
      }
    } catch (error) {
      alert('Error opening refund receipt. Please try again.')
    }
  }

  /**
   * Open merchant receipt in print window
   * Uses /receipt/{receiptId}?type=MERCHANT&print=1 as per API spec,
   * deriving the base URL from receipt_url when available.
   * NOTE: Merchant receipts are NOT available for refunds - this method should never be called with refund receipts
   */
  static printMerchantReceipt(receiptData: Receipt): void {
    try {
      // Safety check: merchant receipts don't exist for refunds
      if (this.isRefundReceipt(receiptData)) {
        alert('Merchant receipts are not available for refund payments')
        return
      }

      let printUrl: string

      if (receiptData.receipt_url) {
        const url = new URL(receiptData.receipt_url, window.location.origin)
        url.pathname = `/receipt/${receiptData.receipt_id}`
        url.searchParams.set('type', 'MERCHANT')
        url.searchParams.set('print', '1')
        printUrl = url.toString()
      } else {
        printUrl = `/receipt/${receiptData.receipt_id}?type=MERCHANT&print=1`
      }

      const printWindow = window.open(printUrl, '_blank')
      if (!printWindow) {
        alert('Failed to open print window. Please check if popups are blocked.')
      }
    } catch (error) {
      alert('Error opening merchant receipt. Please try again.')
    }
  }

  /**
   * Open merchant receipt for display (without auto-print)
   * Uses /receipt/{receiptId}?type=MERCHANT
   * NOTE: Merchant receipts are NOT available for refunds - this method should never be called with refund receipts
   */
  static viewMerchantReceipt(receiptData: Receipt): void {
    try {
      // Safety check: merchant receipts don't exist for refunds
      if (this.isRefundReceipt(receiptData)) {
        alert('Merchant receipts are not available for refund payments')
        return
      }

      let viewUrl: string

      if (receiptData.receipt_url) {
        const url = new URL(receiptData.receipt_url, window.location.origin)
        url.pathname = `/receipt/${receiptData.receipt_id}`
        url.searchParams.set('type', 'MERCHANT')
        url.searchParams.delete('print')
        viewUrl = url.toString()
      } else {
        viewUrl = `/receipt/${receiptData.receipt_id}?type=MERCHANT`
      }

      window.open(viewUrl, '_blank')
    } catch (error) {
      alert('Error opening merchant receipt. Please try again.')
    }
  }

  /**
   * Open receipt for display (without auto-print)
   */
  static viewReceipt(receiptData: Receipt): void {
    try {
      // Refund receipts use dedicated refund route without type
      if (this.isRefundReceipt(receiptData)) {
        const viewUrl = this.buildRefundReceiptUrl(receiptData, false)
        window.open(viewUrl, '_blank')
        return
      }

      let viewUrl: string

      if (receiptData.receipt_url) {
        // Normalize to /receipt/{id}?type=CUSTOMER (no print flag)
        const url = new URL(receiptData.receipt_url, window.location.origin)
        url.pathname = `/receipt/${receiptData.receipt_id}`
        url.searchParams.set('type', 'CUSTOMER')
        url.searchParams.delete('print')
        viewUrl = url.toString()
      } else {
        viewUrl = `/receipt/${receiptData.receipt_id}?type=CUSTOMER`
      }

      window.open(viewUrl, '_blank')
    } catch (error) {
      alert('Error opening receipt. Please try again.')
    }
  }

  /**
   * Copy receipt URL to clipboard for sharing
   */
  static async shareReceipt(receiptData: Receipt): Promise<boolean> {
    try {
      // Refund receipts share the refund URL without type
      if (this.isRefundReceipt(receiptData)) {
        const shareUrl = this.buildRefundReceiptUrl(receiptData, false)
        await navigator.clipboard.writeText(shareUrl)
        return true
      }

      if (!receiptData.receipt_url) {
        alert('Receipt URL not available for sharing.')
        return false
      }

      // Normalize to /receipt/{id}?type=CUSTOMER (no print flag) before sharing
      const url = new URL(receiptData.receipt_url, window.location.origin)
      url.pathname = `/receipt/${receiptData.receipt_id}`
      url.searchParams.set('type', 'CUSTOMER')
      url.searchParams.delete('print')
      const shareUrl = url.toString()

      await navigator.clipboard.writeText(shareUrl)
      return true
    } catch (error) {
      alert('Error copying receipt URL. Please try again.')
      return false
    }
  }
}
